<script setup lang="ts">
import type { PropertyOnPage } from '@@/shared/property/api-types';

const props = defineProps<{
  property: PropertyOnPage
  title: string
}>();

const emit = defineEmits<{
  share: []
  contact: []
}>();

const { t } = useI18n();

const hasPhotos = computed(() => props.property.photos.length > 0);
const hasMedia = computed(() => {
  if (!hasPhotos.value) return false;
  if (props.property.photos.length > 1) return true;
  return (props.property.videos?.length ?? 0) > 0 || (props.property.virtualTours?.length ?? 0) > 0;
});

const displayMediaDialog = ref(false);
function openMediaDialog() {
  if (!hasMedia.value) return;
  displayMediaDialog.value = true;
}

// DISPLAY
const runtimeConfig = useRuntimeConfig();

const IMG_WIDTH = 390;
const coverPhoto = computed(() => {
  if (!hasPhotos.value) return;
  const firstPhoto = props.property.photos.at(0);
  if (!firstPhoto) return;
  return getImageResize({
    src: firstPhoto,
    width: IMG_WIDTH,
    resizeHostname: runtimeConfig.public.img.resizeHostname,
    factors: 4,
  });
});

const mediaButtonLabel = computed(() => {
  if (!hasMedia.value) return '';
  const { photos, videos, virtualTours } = props.property;
  const photoCount = photos.length;
  if (videos?.length && virtualTours?.length) return t('property.gallery.all_media.count', { count: photoCount, photoCount });
  if (videos?.length) return t('property.gallery.photo_and_video.count', { count: photoCount, photoCount });
  if (virtualTours?.length) return t('property.gallery.photo_and_virtual_tour.count', { count: photoCount, photoCount });
  return t('property.gallery.photo.count', { count: photoCount });
});
</script>

<template>
  <figure
    class="relative isolate"
    :class="{
      'h-96 pb-l': hasPhotos,
    }"
  >
    <img
      v-if="hasPhotos"
      v-bind="coverPhoto"
      class="w-full h-full object-contain"
      :width="String(IMG_WIDTH)"
      :alt="title"
      @click="openMediaDialog"
    >
    <IadTag
      v-if="property.isExclusive"
      size="large"
      :class="{
        'absolute top-xs left-xs z-20': hasPhotos,
      }"
      :text="$t(`property.tags.exclusive`)"
    />
    <IadBtn
      v-if="hasMedia"
      class="absolute right-0 -mt-xl mx-m"
      variant="outlined"
      @click="openMediaDialog"
    >
      <Icon name="iad-icon:image" />
      <span v-text="mediaButtonLabel" />
    </IadBtn>
    <ClientOnly>
      <PropertyDialogMedia
        v-model="displayMediaDialog"
        :property
        :title
        @share="emit('share')"
        @contact="emit('contact')"
      />
    </ClientOnly>
  </figure>
</template>
